// Trois cartes en polygones prédéfinis (dossier §3). Le niveau de danger d'un point est le plus haut
// niveau des polygones qui le contiennent ; le pourtour gris reste continu. Les frontières sont traversables.
// Géométrie initiale proposée sans le dessin original de Léni : à ajuster librement ici.
import { ARENA_HALF } from './constants';
import type { ZoneLevel } from './shapes';

export type Poly = number[]; // [x0, y0, x1, y1, ...]

export interface ZoneRegion {
  /** Identifiant stable de la région (territoire des monstres). */
  id: string;
  level: ZoneLevel;
  poly: Poly;
  /** Boîte englobante pour accélérer les tests. */
  box: [number, number, number, number];
}

export interface MapDef {
  id: 'A' | 'B' | 'C' | 'T';
  name: string;
  regions: ZoneRegion[];
}

function octagon(cx: number, cy: number, rx: number, ry = rx, rot = Math.PI / 8): Poly {
  const p: Poly = [];
  for (let i = 0; i < 8; i++) {
    const a = rot + (i * Math.PI) / 4;
    p.push(Math.round(cx + Math.cos(a) * rx), Math.round(cy + Math.sin(a) * ry));
  }
  return p;
}

function region(id: string, level: ZoneLevel, poly: Poly): ZoneRegion {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (let i = 0; i < poly.length; i += 2) {
    x0 = Math.min(x0, poly[i]);
    x1 = Math.max(x1, poly[i]);
    y0 = Math.min(y0, poly[i + 1]);
    y1 = Math.max(y1, poly[i + 1]);
  }
  return { id, level, poly, box: [x0, y0, x1, y1] };
}

/** Carte A : poches dispersées — plusieurs zones vertes, poches orange et rouges distinctes, boss au centre. */
const MAP_A: MapDef = {
  id: 'A',
  name: 'Archipel',
  regions: [
    region('A-g1', 1, octagon(-2350, -2350, 900)),
    region('A-g2', 1, octagon(2350, -2350, 900)),
    region('A-g3', 1, octagon(-2350, 2350, 900)),
    region('A-g4', 1, octagon(2350, 2350, 900)),
    region('A-g5', 1, octagon(-2500, 0, 700, 1000)),
    region('A-g6', 1, octagon(2500, 0, 700, 1000)),
    region('A-o1', 2, octagon(0, -2400, 1250, 900)),
    region('A-o2', 2, octagon(0, 2400, 1250, 900)),
    region('A-o3', 2, octagon(-1150, 0, 680, 1100)),
    region('A-r1', 3, octagon(1350, -1250, 800)),
    region('A-r2', 3, octagon(1350, 1250, 800)),
    region('A-v', 4, octagon(0, 0, 900)),
  ],
};

/** Carte B : zones imbriquées vers le centre. */
const MAP_B: MapDef = {
  id: 'B',
  name: 'Spirale',
  regions: [
    region('B-g', 1, octagon(0, 0, 3150)),
    region('B-o', 2, octagon(0, 0, 2330)),
    region('B-r', 3, octagon(0, 0, 1640)),
    region('B-v', 4, octagon(0, 0, 950)),
  ],
};

/** Carte C : deux pôles vert/orange/rouge et un boss entre eux. */
const MAP_C: MapDef = {
  id: 'C',
  name: 'Bipôle',
  regions: [
    region('C-gW', 1, octagon(-2100, 0, 1400, 2900)),
    region('C-oW', 2, octagon(-2100, 0, 1000, 2050)),
    region('C-rW', 3, octagon(-2100, 0, 700, 1350)),
    region('C-gE', 1, octagon(2100, 0, 1400, 2900)),
    region('C-oE', 2, octagon(2100, 0, 1000, 2050)),
    region('C-rE', 3, octagon(2100, 0, 700, 1350)),
    region('C-v', 4, octagon(0, 0, 700, 1050)),
  ],
};

export const MAPS: MapDef[] = [MAP_A, MAP_B, MAP_C];

/** Terrain neutre des modes équipes et survie : aucune zone de danger, hors rotation FFA. */
export const MAP_T: MapDef = { id: 'T', name: 'Terrain neutre', regions: [] };

export function getMap(id: string): MapDef {
  if (id === 'T') return MAP_T;
  return MAPS.find((m) => m.id === id) ?? MAP_A;
}

export function pointInPoly(x: number, y: number, p: Poly): boolean {
  let inside = false;
  for (let i = 0, j = p.length - 2; i < p.length; j = i, i += 2) {
    const xi = p[i];
    const yi = p[i + 1];
    const xj = p[j];
    const yj = p[j + 1];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

export function inRegion(r: ZoneRegion, x: number, y: number): boolean {
  const b = r.box;
  return x >= b[0] && x <= b[2] && y >= b[1] && y <= b[3] && pointInPoly(x, y, r.poly);
}

/** Région la plus dangereuse contenant le point (null = gris). */
export function regionAt(map: MapDef, x: number, y: number): ZoneRegion | null {
  let best: ZoneRegion | null = null;
  for (const r of map.regions) if ((!best || r.level > best.level) && inRegion(r, x, y)) best = r;
  return best;
}

export function zoneLevelAt(map: MapDef, x: number, y: number): ZoneLevel {
  if (Math.abs(x) > ARENA_HALF || Math.abs(y) > ARENA_HALF) return 0;
  return regionAt(map, x, y)?.level ?? 0;
}

const sharesCache = new Map<string, number[]>();

/** Répartition de surface par niveau (échantillonnage régulier), en fractions. Mise en cache (cartes statiques). */
export function surfaceShares(map: MapDef, step = 40): number[] {
  const key = `${map.id}:${step}`;
  const cached = sharesCache.get(key);
  if (cached) return cached;
  const res = computeShares(map, step);
  sharesCache.set(key, res);
  return res;
}

const areaCache = new Map<string, Map<string, number>>();

/** Surface exclusive (u²) de chaque région : points où elle est la région la plus dangereuse. */
export function regionAreas(map: MapDef, step = 80): Map<string, number> {
  const key = `${map.id}:${step}`;
  const cached = areaCache.get(key);
  if (cached) return cached;
  const area = new Map<string, number>();
  for (let y = -ARENA_HALF + step / 2; y < ARENA_HALF; y += step)
    for (let x = -ARENA_HALF + step / 2; x < ARENA_HALF; x += step) {
      const r = regionAt(map, x, y);
      if (r) area.set(r.id, (area.get(r.id) ?? 0) + step * step);
    }
  areaCache.set(key, area);
  return area;
}

function computeShares(map: MapDef, step: number): number[] {
  const counts = [0, 0, 0, 0, 0];
  let n = 0;
  for (let y = -ARENA_HALF + step / 2; y < ARENA_HALF; y += step)
    for (let x = -ARENA_HALF + step / 2; x < ARENA_HALF; x += step) {
      counts[zoneLevelAt(map, x, y)]++;
      n++;
    }
  return counts.map((c) => c / n);
}

export const ZONE_COLORS = [0x151a20, 0x2e7d4f, 0xb8732a, 0xb33a3a, 0x7a4bc2] as const;
